import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import requestChatArrow from "@assets/facilityNetwork/request-chat-arrow.svg";
import chevronWhite from "@assets/facilityNetwork/req-chevron-white.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import {
  firstFacilityWord,
  formatExpiresIn,
  formatFacilityTypeLine,
  formatInboxFullDate,
  formatLocationLine,
  resolvedLinkParties,
  wrapReasonQuote,
  type NetworkInboxItem,
} from "./facilityNetworkInboxUtils";
import { FN } from "./facilityNetworkTheme";
import { parentReadScopeAcceptSummary } from "./parentReadAccessScope";

type Props = {
  selected: NetworkInboxItem | null;
  needsCount: number;
  awaitingCount: number;
  submitting: boolean;
  actionError: string | null;
  onClose: () => void;
  onDeclineUnlink: (reason: string) => void;
  onAcceptUnlink: () => void;
  onDeclineLink: (reason: string) => void;
  onAcceptLink: () => void;
  onCancelOutgoing: () => void;
  onSendInvite?: (email: string) => void;
  onResend?: () => void;
};

const PLACEHOLDER_REASON = "e.g. We do not recognize this organization as our parent facility...";

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", gap: "4px", alignItems: "flex-start", flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
      <Typography
        component="span"
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: FN.figmaNeutral,
          lineHeight: "20px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 400,
          color: FN.figmaAccent,
          lineHeight: "20px",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function QuoteBox({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "amber";
}) {
  const border =
    tone === "amber" ? "2px solid #fabc89" : `2px solid ${FN.primary}`;
  const bg = tone === "amber" ? "#fff4e5" : "#f5f5f5";
  return (
    <Box
      sx={{
        width: "100%",
        borderLeft: border,
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ bgcolor: bg, p: "12px", borderRadius: "5px" }}>{children}</Box>
    </Box>
  );
}

function StatusBanner({ text }: { text: string }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        bgcolor: "#f5f5f5",
        borderRadius: "4px",
        px: "12px",
        py: "10px",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          color: FN.figmaAccent,
          letterSpacing: "0.03px",
          lineHeight: "16px",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function GhostButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="outlined"
      disabled={disabled}
      onClick={onClick}
      sx={{
        textTransform: "none",
        height: 32,
        minHeight: 32,
        px: "12px",
        py: "6px",
        borderRadius: "4px",
        borderColor: "#d4d4d4",
        color: FN.text,
        fontFamily: FN.fontFamily,
        fontSize: 14,
        fontWeight: 600,
        bgcolor: "#fff",
        "&:hover": { borderColor: FN.muted, bgcolor: "#fff" },
      }}
    >
      {children}
    </Button>
  );
}

function DangerButton({
  children,
  onClick,
  disabled,
  contained = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  contained?: boolean;
}) {
  return (
    <Button
      variant={contained ? "contained" : "outlined"}
      disabled={disabled}
      onClick={onClick}
      sx={
        contained
          ? {
              textTransform: "none",
              height: 32,
              minHeight: 32,
              px: "12px",
              py: "6px",
              borderRadius: "4px",
              bgcolor: "#d13438",
              color: "#fff",
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
              "&:hover": { bgcolor: "#a4262c" },
              "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
            }
          : {
              textTransform: "none",
              height: 32,
              minHeight: 32,
              px: "12px",
              py: "6px",
              borderRadius: "4px",
              borderColor: FN.figmaErrorStroke,
              color: FN.figmaError,
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#fff",
              "&:hover": { borderColor: FN.figmaError, bgcolor: "#fff8f8" },
            }
      }
    >
      {children}
    </Button>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  submitting,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  submitting?: boolean;
}) {
  return (
    <Button
      variant="contained"
      disabled={disabled}
      onClick={onClick}
      endIcon={
        submitting ? (
          <CircularProgress size={12} sx={{ color: "#fff" }} />
        ) : (
          <FacilityNetworkFigmaIcon src={chevronWhite} size={12} />
        )
      }
      sx={{
        textTransform: "none",
        height: 32,
        minHeight: 32,
        px: "12px",
        py: "6px",
        borderRadius: "4px",
        fontFamily: FN.fontFamily,
        fontSize: 14,
        fontWeight: 600,
        color: "#fff",
        background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
        "&:hover": { background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)" },
        "& .MuiButton-endIcon": { ml: "4px" },
      }}
    >
      {children}
    </Button>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "flex-end",
        gap: 1,
        pt: 1,
        borderTop: `1px solid ${FN.figmaCardBorder}`,
      }}
    >
      {children}
    </Box>
  );
}

function EmptyPane({ needsCount, awaitingCount }: { needsCount: number; awaitingCount: number }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        p: 5,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "64px",
          bgcolor: FN.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FacilityNetworkFigmaIcon src={requestChatArrow} size={28} />
      </Box>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          color: FN.text,
          letterSpacing: FN.figmaTitleTracking,
          lineHeight: "32px",
          textAlign: "center",
        }}
      >
        Select a request to see details
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          color: FN.muted,
          lineHeight: "20px",
          textAlign: "center",
        }}
      >
        {`You have ${needsCount} item${needsCount === 1 ? "" : "s"} needing your response and ${awaitingCount} awaiting others. Click any item to review and take action.`}
      </Typography>
    </Box>
  );
}

export default function FacilityNetworkRequestDetail({
  selected,
  needsCount,
  awaitingCount,
  submitting,
  actionError,
  onClose,
  onDeclineUnlink,
  onAcceptUnlink,
  onDeclineLink,
  onAcceptLink,
  onCancelOutgoing,
  onSendInvite,
  onResend,
}: Props) {
  const [reason, setReason] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [isDeclining, setIsDeclining] = useState(false);

  useEffect(() => {
    setReason("");
    setIsDeclining(false);
    setInviteEmail(selected?.inviteEmail || "");
  }, [selected?.key, selected?.inviteEmail]);

  if (!selected) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          border: `1px solid ${FN.figmaCardBorder}`,
          borderRadius: FN.figmaCardRadius,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          flex: 1,
        }}
      >
        <EmptyPane needsCount={needsCount} awaitingCount={awaitingCount} />
      </Box>
    );
  }

  const name = selected.facilityName;
  const first = firstFacilityWord(name);
  const typeLine = formatFacilityTypeLine(selected.facilityType);
  const location = formatLocationLine(selected.city, selected.state, selected.location);
  const ccn = selected.ccn?.trim() || "NA";
  const npi = selected.npi?.trim() || "NA";
  const sent = formatInboxFullDate(selected.createdAt);
  const expires = formatExpiresIn(selected.expiresInDays);
  const linkParties =
    selected.variant === "resolved_link" ? resolvedLinkParties(selected) : null;
  const bannerColor =
    selected.status === "invite_sent"
      ? FN.figmaWarning
      : selected.variant === "outgoing_link" || selected.variant === "incoming_link"
        ? FN.primary
        : FN.figmaError;

  const bannerText = (() => {
    switch (selected.variant) {
      case "incoming_unlink":
        return "Incoming unlink request";
      case "outgoing_unlink":
        return `This is an outgoing unlink request to ${name}`;
      case "outgoing_link":
        return selected.status === "invite_sent"
          ? `An invitation was sent to ${name}`
          : `This is an outgoing link request to ${name}`;
      case "incoming_link":
        return `This is an incoming link request from ${name}`;
      case "invite_needed":
        return "This facility needs to set up Unicomply.";
      case "resolved_unlink":
        return selected.outcomePill === "approved"
          ? "This is an unlink request which was approved"
          : "This is an unlink request which was rejected";
      case "resolved_link":
        return linkParties?.banner ?? "";
      default:
        return "";
    }
  })();

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${FN.figmaCardBorder}`,
        borderRadius: FN.figmaCardRadius,
        display: "flex",
        flexDirection: "column",
        p: 2,
        gap: 2,
        height: "100%",
        minHeight: 0,
        flex: 1,
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1, overflow: "hidden", width: "100%", maxWidth: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 18,
              fontWeight: 700,
              color: FN.figmaNeutral,
              letterSpacing: "-0.18px",
              lineHeight: 1.5,
            }}
          >
            {selected.variant === "invite_needed" ? `${name} — Not in Unicomply` : name}
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 400,
              color: selected.variant === "invite_needed" ? FN.figmaWarning : bannerColor,
              lineHeight: "20px",
            }}
          >
            {bannerText}
          </Typography>
        </Box>

        {selected.variant === "resolved_unlink" || selected.variant === "resolved_link" ? (
          <Box sx={{ display: "flex", gap: "4px", width: "100%", minWidth: 0, maxWidth: "100%" }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <FieldRow
                label="Type"
                value={
                  selected.kind === "unlink"
                    ? `Unlink request · ${selected.direction}`
                    : `Link request · ${selected.direction}`
                }
              />
              <FieldRow
                label="Outcome"
                value={
                  selected.outcomePill === "rejected"
                    ? "Rejected with a reason"
                    : selected.outcomePill
                      ? selected.outcomePill.charAt(0).toUpperCase() + selected.outcomePill.slice(1)
                      : "—"
                }
              />
              {linkParties ? (
                <>
                  <FieldRow label="Sent by" value={linkParties.sentBy} />
                  <FieldRow label="Sent to" value={linkParties.sentTo} />
                </>
              ) : null}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {linkParties ? (
                <FieldRow label={linkParties.resolvedByLabel} value={linkParties.resolvedBy} />
              ) : null}
              <FieldRow label="Closed" value={formatInboxFullDate(selected.resolvedAt)} />
              <FieldRow label={`${linkParties?.idPrefix ?? ""}CCN`} value={ccn} />
              <FieldRow label={`${linkParties?.idPrefix ?? ""}NPI`} value={npi} />
            </Box>
          </Box>
        ) : selected.variant === "invite_needed" ? (
          <Box sx={{ display: "flex", gap: "4px", width: "100%", minWidth: 0, maxWidth: "100%" }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <FieldRow label="Facility name" value={name} />
              <FieldRow label="Type" value={typeLine} />
              <FieldRow label="CCN" value={ccn} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <FieldRow label="NPI" value={npi} />
              <FieldRow label="Location" value={location} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "4px", width: "100%", minWidth: 0, maxWidth: "100%" }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {selected.status === "invite_sent" ? (
                <FieldRow label="Facility name" value={name} />
              ) : null}
              <FieldRow label="Type" value={typeLine} />
              <FieldRow label="CCN" value={ccn} />
              <FieldRow label="NPI" value={npi} />
              {selected.variant === "incoming_unlink" ? (
                <FieldRow label="Sent" value={sent} />
              ) : selected.variant === "outgoing_link" ? (
                <FieldRow label="Expires in" value={expires} />
              ) : (
                <FieldRow label="Sent" value={sent} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <FieldRow label="Location" value={location} />
              {selected.variant === "incoming_unlink" ? (
                <>
                  <FieldRow label="Currently" value={selected.currentlyLabel || "—"} />
                  <FieldRow label="Expires in" value={expires} />
                </>
              ) : selected.variant === "outgoing_link" ? (
                <>
                  <FieldRow label="Sent" value={sent} />
                  <FieldRow
                    label="Sent to"
                    value={selected.inviteEmail?.trim() || "Facility admins"}
                  />
                </>
              ) : selected.variant === "incoming_link" ? (
                <FieldRow label="Expires in" value={expires} />
              ) : null}
            </Box>
          </Box>
        )}

        {selected.variant === "incoming_unlink" ? (
          <>
            {selected.reason?.trim() ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                  Their reason
                </Typography>
                <QuoteBox tone="amber">
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      fontStyle: "italic",
                      color: "#8b4409",
                      lineHeight: "16px",
                    }}
                  >
                    {wrapReasonQuote(selected.reason)}
                  </Typography>
                </QuoteBox>
              </Box>
            ) : null}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                Reason for disapproval{" "}
                <Box component="span" sx={{ color: FN.figmaError }}>
                  *
                </Box>
              </Typography>
              <QuoteBox>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={PLACEHOLDER_REASON}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      color: FN.figmaAccent,
                      letterSpacing: "0.03px",
                      lineHeight: "16px",
                    },
                  }}
                />
              </QuoteBox>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                What happens next
              </Typography>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.figmaAccent, lineHeight: "20px" }}>
                {`If you approve, the link terminates immediately and ${name} leaves your network. Historic data remains queryable. If you disapprove, the link stays active and ${first} sees your reason.`}
              </Typography>
            </Box>
          </>
        ) : null}

        {selected.variant === "outgoing_link" ? (
          <>
            <StatusBanner
              text={`Awaiting response · will auto-expire in ${formatExpiresIn(selected.expiresInDays)}`}
            />
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.figmaAccent, lineHeight: "20px" }}>
              {selected.status === "invite_sent"
                ? `An invite was sent to ${selected.inviteEmail || "the facility administrator"}. Once they sign up, they can accept your link request.`
                : `Cancelling withdraws the request. No notification is sent to ${first}; the request simply disappears from their queue. You can send a new request later.`}
            </Typography>
            {selected.coordinationMode ? (
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.figmaAccent,
                  lineHeight: "16px",
                  opacity: 0.85,
                }}
              >
                This request involves coordination with the target facility&apos;s current setup.
                You&apos;ll be notified when it resolves — typically within a few days.
              </Typography>
            ) : null}
          </>
        ) : null}

        {selected.variant === "outgoing_unlink" ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                Your Reason
              </Typography>
              <QuoteBox>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    fontStyle: "italic",
                    color: FN.figmaAccent,
                    lineHeight: "16px",
                    letterSpacing: "0.03px",
                  }}
                >
                  {wrapReasonQuote(selected.reason)}
                </Typography>
              </QuoteBox>
            </Box>
            <StatusBanner
              text={`Awaiting ${first}'s approval · expires in ${formatExpiresIn(selected.expiresInDays)} · link remains active until approved`}
            />
          </>
        ) : null}

        {selected.variant === "invite_needed" ? (
          <>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                color: FN.figmaAccent,
                lineHeight: "20px",
              }}
            >
              This facility has a valid CCN but hasn&apos;t set up Unicomply yet. Provide an email address
              to send them an invite. Once they sign up, they&apos;ll see your pending link request and can
              accept it.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: FN.figmaNeutral,
                  lineHeight: "20px",
                }}
              >
                Email ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="admin@facility.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={submitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                    borderRadius: "8px",
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    color: FN.text,
                    boxShadow: "0px 2px 2px rgba(194,194,194,0.25)",
                    "& fieldset": {
                      borderColor: "#e5e5e5",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d4d4d4",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: FN.primary,
                    },
                    "& input::placeholder": {
                      color: FN.muted,
                      opacity: 1,
                    },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.figmaAccent,
                  lineHeight: "16px",
                  letterSpacing: "0.03px",
                }}
              >
                Best to use a compliance officer or administrator address. Personal Gmail-style addresses often go to spam.
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: "#fff4e5",
                border: "1px solid #fabc89",
                borderRadius: "4px",
                p: "8px 12px",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  color: "#8b4409",
                  lineHeight: "20px",
                }}
              >
                Invite expires in 15 days if not accepted
              </Typography>
            </Box>
          </>
        ) : null}

        {selected.variant === "incoming_link" ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                What accepting means
              </Typography>
              <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.figmaAccent, lineHeight: "20px" }}>
                {parentReadScopeAcceptSummary()}
              </Typography>
            </Box>

            {selected.coordinationMode && selected.currentParentFacilityName ? (
              <Box
                sx={{
                  bgcolor: "#fff4e5",
                  border: "1px solid #fabc89",
                  borderRadius: "4px",
                  p: "8px 12px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    color: "#8b4409",
                    lineHeight: "20px",
                  }}
                >
                  {selected.coordinationUnlinkId
                    ? `Unlink request sent to ${selected.currentParentFacilityName}. This link completes only if they approve.`
                    : `Accepting sends an unlink request to your current parent ${selected.currentParentFacilityName}. The link with ${name} only establishes if ${selected.currentParentFacilityName} approves the unlink. If ${selected.currentParentFacilityName} disapproves, this request is cancelled and you remain linked to ${selected.currentParentFacilityName}.`}
                </Typography>
              </Box>
            ) : null}

            {!selected.coordinationUnlinkId && isDeclining ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
                  Reason for declining{" "}
                  <Box component="span" sx={{ color: FN.figmaError }}>
                    *
                  </Box>
                </Typography>
                <QuoteBox>
                  <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder={PLACEHOLDER_REASON}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: FN.fontFamily,
                        fontSize: 12,
                        color: FN.figmaAccent,
                        letterSpacing: "0.03px",
                        lineHeight: "16px",
                      },
                    }}
                  />
                </QuoteBox>
              </Box>
            ) : null}
          </>
        ) : null}

        {(selected.variant === "resolved_unlink" || selected.variant === "resolved_link") &&
        (selected.resolutionNote?.trim() || selected.reason?.trim()) ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, fontWeight: 600, color: FN.figmaNeutral }}>
              {selected.variant === "resolved_link" && selected.direction === "outgoing"
                ? "Reason for declining"
                : "Your Reason"}
            </Typography>
            <QuoteBox>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  fontStyle: "italic",
                  color: FN.figmaAccent,
                  lineHeight: "16px",
                  letterSpacing: "0.03px",
                }}
              >
                {wrapReasonQuote(selected.resolutionNote || selected.reason)}
              </Typography>
            </QuoteBox>
          </Box>
        ) : null}

        {actionError ? (
          <Alert severity="error" sx={{ py: 0 }}>
            {actionError}
          </Alert>
        ) : null}
      </Box>

      {selected.variant === "incoming_unlink" ? (
        <Footer>
          <GhostButton disabled={submitting} onClick={onClose}>
            Cancel
          </GhostButton>
          <DangerButton
            disabled={submitting || !reason.trim()}
            onClick={() => onDeclineUnlink(reason.trim())}
          >
            Disapprove
          </DangerButton>
          <PrimaryButton disabled={submitting} submitting={submitting} onClick={onAcceptUnlink}>
            Approve unlink
          </PrimaryButton>
        </Footer>
      ) : null}

      {selected.variant === "incoming_link" ? (
        <Footer>
          <GhostButton disabled={submitting} onClick={onClose}>
            {selected.coordinationUnlinkId ? "Close" : "Cancel"}
          </GhostButton>
          {!selected.coordinationUnlinkId && !isDeclining ? (
            <DangerButton disabled={submitting} onClick={() => setIsDeclining(true)}>
              Decline
            </DangerButton>
          ) : null}
          {!selected.coordinationUnlinkId && isDeclining ? (
            <DangerButton
              contained
              disabled={submitting || !reason.trim()}
              onClick={() => onDeclineLink(reason.trim())}
            >
              Confirm decline
            </DangerButton>
          ) : null}
          {!selected.coordinationUnlinkId && !isDeclining ? (
            <PrimaryButton disabled={submitting} submitting={submitting} onClick={onAcceptLink}>
              {selected.coordinationMode ? "Accept and request unlink" : "Accept and Link"}
            </PrimaryButton>
          ) : null}
        </Footer>
      ) : null}

      {selected.variant === "invite_needed" ? (
        <Footer>
          <GhostButton disabled={submitting} onClick={onClose}>
            Cancel
          </GhostButton>
          <PrimaryButton
            disabled={submitting || !inviteEmail.trim()}
            submitting={submitting}
            onClick={() => onSendInvite?.(inviteEmail.trim())}
          >
            Send Invite
          </PrimaryButton>
        </Footer>
      ) : null}

      {selected.variant === "outgoing_link" ||
      selected.variant === "outgoing_unlink" ? (
        <Footer>
          <GhostButton disabled={submitting} onClick={onClose}>
            Close
          </GhostButton>
          {selected.status !== "invite_sent" ? (
            <DangerButton disabled={submitting} onClick={onCancelOutgoing}>
              Cancel Request
            </DangerButton>
          ) : null}
        </Footer>
      ) : null}

      {selected.bucket === "resolved" ? (
        <Footer>
          <GhostButton disabled={submitting} onClick={onClose}>
            Close
          </GhostButton>
          {selected.status === "expired" ? (
            <PrimaryButton
              disabled={submitting}
              submitting={submitting}
              onClick={() => onResend?.()}
            >
              Resend
            </PrimaryButton>
          ) : null}
        </Footer>
      ) : null}
    </Box>
  );
}
